import React, { Component } from 'react'
import { View, TextInput, Keyboard, Platform } from 'react-native'
import styled from 'styled-components/native'

import { Text } from '../../Atoms/Typography'
import PageHeader from '../../Molecules/PageHeader'
import InlineError from '../../Atoms/InlineError'
import TranslatedText from '../../Atoms/TranslatedText'
import AssetsProfileChart from './AssetsProfileChart'
import { thousandSeparator } from '../../../lib/formatters'
import { translate } from '../../../services/translate'
import theme from '../../../styles/theme'

const CHART_HEIGHT = 420

const Container = styled(View)`
  flex: 1;
  flex-direction: column;
`

const Content = styled(View)`
  flex: 1;
  flex-direction: column;
`

const ChartContainer = styled(View)`
  width: 100%;
  margin-top: ${2 * theme.gutterUnit}px;
  flex-direction: row;
`

const TotalContainer = styled(View)`
  flex: 1;
  align-items: center;
`

const InputsContainer = styled(View)`
  flex: 5;
`

const LegendContainer = styled(View)`
  height: ${CHART_HEIGHT}px;
  flex: 1;
  align-items: flex-start;
  justify-content: space-between;
  padding: ${10 * theme.gutterUnit}px ${theme.gutterUnit}px;
  border-color: ${theme.colours.bordergrey};
  borderTopWidth: 1px;
  borderBottomWidth: 1px;
  background-color: ${theme.colours.white};
`

const LegendDot = styled(View)`
  width: 20px;
  height: 20px;
  border-radius: 10px;
  background-color: ${props => props.colour};
  margin-right: ${theme.gutterUnit}px;
`

const LegendLine = styled(View)`
  flex-direction: row;
`

const BarChartContainer = styled(View)`
  height: ${CHART_HEIGHT}px;
  flex: 5;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
  padding-top: ${theme.gutterUnit}px;
  border-color: ${theme.colours.bordergrey};
  border-width: 1px;
  background-color: ${theme.colours.white};
`

const ManageFundingContainer = styled(View)`
  border-width: 1px;
  border-color: ${theme.colours.bordergrey};
  flex-direction: row;
  background-color: ${theme.colours.white};
`

const Row = styled(View)`
  flex-direction: row;
  padding: ${theme.gutterUnit}px;
  justify-content: center;
  align-items: center;
`

const YearLabelRow = styled(View)`
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding-left: ${theme.gutterUnit}px;
  padding-right: ${theme.gutterUnit}px;
`

const LabelColumn = styled(View)`
  align-items: flex-start;
  width: 75px;
`
const ValuesRow = styled(View)`
  flex-direction: row;
  flex: 1;
  padding-right: ${3 * theme.gutterUnit}px;
`

const Column = styled(View)`
  width: 20%;
  justify-content: center;
  align-items: center;
`

const StyledInput = styled(TextInput)`
  height: 40px;
  width: 95px;
  border-color: ${props => props.editable ? theme.colours.grey : theme.colours.grey};
  background-color: ${props => props.editable ? theme.colours.blue : theme.colours.grey};
  border-width: 1px;
  border-radius: 5px;
  color: ${props => props.editable ? theme.colours.white : theme.colours.black}

`

const formatValue = (val, input = false) => {
  if (val === 0) {
    return '0'
  }
  if (Platform.OS === 'web' && input) {
    return `${val}`
  }
  if (val < 0) {
    return `(${thousandSeparator(val * -1)})`
  }
  return `${thousandSeparator(val)}`
}

const EXISTING_FUNDING_COLOUR = '#93d8fc'
const MAX_WSF_CHANGE = 100000

export default class TreasuryBalanceSheet extends Component {
  state = {
    error: null,
    barChartWidth: null
  }

  constructor (props) {
    super(props)
    this.textInputs = {}
  }

  shouldComponentUpdate (nextProps, nextState) {
    if (!nextProps.active) {
      return false
    }
    return true
  }

  componentDidMount () {
    this.keyboardDidShowListener = Keyboard.addListener('keyboardDidShow', this._keyboardDidShow)
    this.keyboardDidHideListener = Keyboard.addListener('keyboardDidHide', this._keyboardDidHide)
  }

  componentWillUnmount () {
    this.keyboardDidShowListener.remove()
    this.keyboardDidHideListener.remove()
  }

  handleLayout = event => {
    const { width } = event.nativeEvent.layout
    if (this.state.barChartWidth === width) return // layout was already called

    this.setState({
      barChartWidth: width
    })
  }

  _keyboardDidShow = () => {
    if (!this.props.active) {
      return
    }
    this.setState({ error: null })
    this.bid = 0
  }

  _keyboardDidHide = () => {
    if (!this.props.active) {
      return
    }
    if (this.tempValue == null || typeof this.tempValue !== 'string') {
      return
    }
    let tempValue = this.tempValue.replace(/,/i, '')
    tempValue = tempValue.replace('(', '-').replace(')', '')
    if (isNaN(tempValue) || parseInt(tempValue).toString() !== tempValue) {
      return this.setState({ error: 'Input must be a positive whole number' })
    }
    let val = parseInt(tempValue)
    if (val < 0) {
      this.setState({ error: 'Input must be a positive whole number' })
      return this.props.onChangeValue({
        forceRerender: new Date().valueOf()
      })
    }
    const { treasury: { openingBalances }, decisions } = this.props
    const total = decisions.wsf1 + decisions.wsf2 + decisions.wsf3 + decisions.wsf4 + decisions.wsf5 - decisions[this.tempType] + val
    let wsfChange = total - Math.abs(openingBalances.wsfRepayments)
    const amountOverMax = wsfChange - (MAX_WSF_CHANGE - Math.abs(openingBalances.wsfRepayments))
    let forceRerender = false
    if (amountOverMax > 0) {
      this.setState({ error: 'Maximum wholesale funding movement exceeded' })
      wsfChange = MAX_WSF_CHANGE - Math.abs(openingBalances.wsfRepayments)
      val = val - amountOverMax
      if (val === decisions[this.tempType]) {
        forceRerender = true
      }
    }

    const thingsToChange = {
      wsfChange,
      [this.tempType]: parseInt(val)
    }

    if (forceRerender) {
      thingsToChange.forceRerender = new Date().valueOf()
    }

    this.tempType = null
    this.tempValue = null
    this.props.onChangeValue(thingsToChange)
  }

  onInput = (type, val) => {
    this.tempValue = val
    this.tempType = type
  }

  onFocus = (type) => {
    this.setState({ error: null })
    if (type && this.tempType && type !== this.tempType) {
      this._keyboardDidHide()
    }
  }

  onBlur = (type) => {
    this._keyboardDidHide()
  }

  renderInput = (field, defaultValue) => {
    return (
      <StyledInput
        key={`${field}Input${this.props.forceRerender}`}
        onChangeText={(val) => this.onInput(field, val)}
        defaultValue={formatValue(defaultValue, true)}
        // value={this.props.forceRerender ? formatValue(defaultValue) : undefined}
        autoCorrect={false}
        underlineColorAndroid='transparent'
        keyboardType='numeric'
        multiline={Platform.OS !== 'web'}
        editable={this.props.enableChanges}
        onFocus={() => this.onFocus(field)}
        onBlur={() => this.onBlur(field)}
        style={{ textAlign: 'center', fontFamily: 'ubuntu-medium', fontSize: 18 }}
      />
    )
  }

  render () {
    const { treasury: { fundingProfile, denomination }, decisions } = this.props
    // const wsfMovementAndRepayments = decisions.wsfChange + Math.abs(openingBalances.wsfRepayments)
    const newFundingTotal = decisions.wsf1 + decisions.wsf2 + decisions.wsf3 + decisions.wsf4 + decisions.wsf5
    const existingFundingTotal = fundingProfile.wsfYr1 + fundingProfile.wsfYr2 + fundingProfile.wsfYr3 + fundingProfile.wsfYr4 + fundingProfile.wsfYr5

    return (
      <Container>
        <PageHeader text={translate('treasury.screen2.screenHeader', 'WHOLESALE FUNDING PROFILE VS ASSETS').toUpperCase()} />
        {this.state.error && (
          <InlineError message={this.state.error} />
        )}
        <Content>
          <ManageFundingContainer>
            <InputsContainer>
              <Row><Text medium>{`${translate('treasury.screen2.screenSubHeader', 'MANAGE FUNDING').toUpperCase()} (${denomination})`}</Text></Row>
              <YearLabelRow>
                <LabelColumn><Text>{' '}</Text></LabelColumn>
                <ValuesRow>
                  <Column><Text center darkgrey size='s'>{`1 ${translate('general.year', 'YEAR').toUpperCase()}`}</Text></Column>
                  <Column><Text center darkgrey size='s'>{`2 ${translate('general.year', 'YEAR').toUpperCase()}`}</Text></Column>
                  <Column><Text center darkgrey size='s'>{`3 ${translate('general.year', 'YEAR').toUpperCase()}`}</Text></Column>
                  <Column><Text center darkgrey size='s'>{`4 ${translate('general.year', 'YEAR').toUpperCase()}`}</Text></Column>
                  <Column><Text center darkgrey size='s'>{`5 ${translate('general.year', 'YEAR').toUpperCase()}`}</Text></Column>
                </ValuesRow>
              </YearLabelRow>
              <Row>
                <LabelColumn><Text>{`${translate('treasury.screen2.newLabel', 'New')}:`}</Text></LabelColumn>
                <ValuesRow>
                  <Column>{this.renderInput('wsf1', decisions.wsf1)}</Column>
                  <Column styles={{ }}>{this.renderInput('wsf2', decisions.wsf2)}</Column>
                  <Column>{this.renderInput('wsf3', decisions.wsf3)}</Column>
                  <Column>{this.renderInput('wsf4', decisions.wsf4)}</Column>
                  <Column>{this.renderInput('wsf5', decisions.wsf5)}</Column>
                </ValuesRow>
              </Row>
              <Row>
                <LabelColumn><Text>{`${translate('treasury.screen2.existingLabel', 'Existing')}:`}</Text></LabelColumn>
                <ValuesRow>
                  <Column><Text>{formatValue(fundingProfile.wsfYr1)}</Text></Column>
                  <Column><Text>{formatValue(fundingProfile.wsfYr2)}</Text></Column>
                  <Column><Text>{formatValue(fundingProfile.wsfYr3)}</Text></Column>
                  <Column><Text>{formatValue(fundingProfile.wsfYr4)}</Text></Column>
                  <Column><Text>{formatValue(fundingProfile.wsfYr5)}</Text></Column>
                </ValuesRow>
              </Row>
            </InputsContainer>
            <TotalContainer>
              <Row><Text medium>TOTAL</Text></Row>
              <YearLabelRow style={{ height: 23 }} />
              <Row style={{ height: 55 }}><Text medium>{formatValue(newFundingTotal)}</Text></Row>
              <Row><Text medium>{formatValue(existingFundingTotal)}</Text></Row>
            </TotalContainer>
          </ManageFundingContainer>
          <ChartContainer>
            <BarChartContainer onLayout={this.handleLayout}>
              {this.state.barChartWidth && (
                <AssetsProfileChart
                  decisions={decisions}
                  fundingProfile={fundingProfile}
                  height={CHART_HEIGHT}
                  width={this.state.barChartWidth}
                  denomination={denomination}
                />)}
            </BarChartContainer>
            <LegendContainer>
              <LegendLine>
                <LegendDot colour={theme.colours.orange} />
                <TranslatedText light size='s' value='treasury.screen2.totalAssets' default='Total Assets' />
              </LegendLine>
              <LegendLine>
                <LegendDot colour={theme.colours.blue} />
                <TranslatedText light size='s' value='treasury.screen2.newFunding' default='New Funding' />
              </LegendLine>
              <LegendLine>
                <LegendDot colour={EXISTING_FUNDING_COLOUR} />
                <TranslatedText light size='s' value='treasury.screen2.existingFunding' default='Existing Funding' />
              </LegendLine>
              <LegendLine>
                <LegendDot colour={theme.colours.aqua} />
                <TranslatedText light size='s' value='treasury.screen2.deposits' default='Deposits' />
              </LegendLine>
            </LegendContainer>
          </ChartContainer>

        </Content>
      </Container>
    )
  }
}
